import { useState, useEffect, useRef, useCallback, useMemo, useReducer } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { useSocket } from './useSocket';
import { useMapManager } from './useMapManager';
import { useTokenSession } from './useTokenSession';
import { useGridSession } from './useGridSession';
import { MODE, CURSOR_MODE } from '../constants/gameConstants';
import { PAINT_MODE, PAINT_CATEGORY } from '../data/paintObjects';
import { getBattlemapById } from '../services/battlemapService';
import {
  createBaseCoin,
  sortInitiativeOrder,
  addCoinsToState,
  createCoinsFromEncounter,
} from '../utils/tokenUtils';
import {
  syncActiveMapState,
  gameSessionReducer,
  createGameSessionDispatchSetters,
  extractGameState,
  DEFAULT_MAP_STATE,
  gameStatePatchFromMapManagerInit,
  buildClearGridGameState,
  GAME_SESSION_PATCH,
  applyRoomStateFromResponse,
  saveSettingsFlow,
  leaveRoomFlow,
  resetClientMapsAndSessionAfterLeavingRoom,
  saveMapEditorFlow,
  duplicateMapFlow,
  deleteMapFlow,
} from '../utils/gameStateManager';

/**
 * Owns all game session state, composes feature hooks (map, socket, grid, token session),
 * and builds the object passed to GameContext.Provider.
 *
 * Domain slices passed into hooks (see each hook’s JSDoc):
 * - grid   — geometry, terrain, walls, placed objects, battlemap id
 * - tokens — coins, initiative, selected token
 * - room   — lobby identity + permissions (+ setters for socket)
 * - maps   — map list setters (state lives in useMapManager)
 * - sync   — { roomId, viewingMapId, currentMapId, socketHook } for active-map emits
 */
export function useGameSession() {
  const [gameState, dispatchGameState] = useReducer(
    gameSessionReducer,
    null,
    () => extractGameState(DEFAULT_MAP_STATE)
  );

  const {
    setGridWidth,
    setGridHeight,
    setGridColors,
    setGridWalls,
    setGridObjects,
    setGridObjectInstances,
    setCoins,
    setInitiativeOrder,
    setSelectedBattlemapId,
  } = useMemo(() => createGameSessionDispatchSetters(dispatchGameState), [dispatchGameState]);

  const {
    gridWidth,
    gridHeight,
    gridColors,
    gridWalls,
    gridObjects,
    gridObjectInstances,
    coins,
    initiativeOrder,
    battlemapId: selectedBattlemapId,
  } = gameState;

  const [showColorModal, setShowColorModal] = useState(false);
  const [selectedPaintColor, setSelectedPaintColor] = useState({ clearColor: true, eraseType: 'terrain' });
  const [showPaintModal, setShowPaintModal] = useState(false);
  const [mode, setMode] = useState(MODE.CURSOR);
  const [cursorMode, setCursorMode] = useState(CURSOR_MODE.GAME);
  const [paintMode, setPaintMode] = useState(PAINT_MODE.LINE);
  const [categoryMode, setCategoryMode] = useState(PAINT_CATEGORY.ERASE);
  const [selectedCoinId, setSelectedCoinId] = useState(null);
  const [selectedObjectId, setSelectedObjectId] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [newlyCreatedCoinId, setNewlyCreatedCoinId] = useState(null);

  const [showHealthDialog, setShowHealthDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showStatBlockDialog, setShowStatBlockDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  const [roomId, setRoomId] = useState(null);
  const [userName, setUserName] = useState('');
  const [connectedUsers, setConnectedUsers] = useState([]);
  const [roomCreator, setRoomCreator] = useState('');
  const [permissions, setPermissions] = useState({
    allowCharacterManagement: true,
    allowEnemyManagement: false,
    allowPainting: false,
  });
  const [showRoomDeletedDialog, setShowRoomDeletedDialog] = useState(false);
  const [roomDeletedMessage, setRoomDeletedMessage] = useState('');

  const navigate = useNavigate();
  const gameContainerRef = useRef(null);
  const gameGridRef = useRef(null);
  const gameSidebarRef = useRef(null);
  const updateTimeout = useRef(null);

  const isRoomCreator = userName === roomCreator;

  const room = {
    roomId,
    setRoomId,
    userName,
    setUserName,
    connectedUsers,
    setConnectedUsers,
    roomCreator,
    setRoomCreator,
    permissions,
    setPermissions,
    isRoomCreator,
  };

  const grid = useMemo(
    () => ({
      gridWidth,
      setGridWidth,
      gridHeight,
      setGridHeight,
      gridColors,
      setGridColors,
      gridWalls,
      setGridWalls,
      gridObjects,
      setGridObjects,
      gridObjectInstances,
      setGridObjectInstances,
      selectedBattlemapId,
      setSelectedBattlemapId,
      dispatchGameState,
    }),
    [
      gridWidth,
      gridHeight,
      gridColors,
      gridWalls,
      gridObjects,
      gridObjectInstances,
      selectedBattlemapId,
      setGridWidth,
      setGridHeight,
      setGridColors,
      setGridWalls,
      setGridObjects,
      setGridObjectInstances,
      setSelectedBattlemapId,
      dispatchGameState,
    ]
  );

  const tokens = {
    coins,
    setCoins,
    initiativeOrder,
    setInitiativeOrder,
    selectedCoinId,
    setSelectedCoinId,
  };

  const mapManager = useMapManager({
    grid,
    tokens,
    room,
    socketHook: null,
  });

  const {
    maps,
    setMaps,
    currentMapId,
    setCurrentMapId,
    viewingMapId,
    setViewingMapId,
    handleViewMap,
    handleActivateMap: handleActivateMapInternal,
    isActivatingFromLocalRef,
    initialState,
    cancelPendingMapPersistence,
  } = mapManager;

  const mapsSlice = {
    setMaps,
    setCurrentMapId,
    setViewingMapId,
  };

  const getViewingMapId = useCallback(() => viewingMapId, [viewingMapId]);

  const purgeLocalSessionAfterRoomEnd = useCallback(() => {
    if (isRoomCreator) return;
    resetClientMapsAndSessionAfterLeavingRoom({
      dispatchGameState,
      setMaps,
      setCurrentMapId,
      setViewingMapId,
      cancelPendingMapLocalStorageSave: cancelPendingMapPersistence,
    });
  }, [
    isRoomCreator,
    dispatchGameState,
    setMaps,
    setCurrentMapId,
    setViewingMapId,
    cancelPendingMapPersistence,
  ]);

  useEffect(() => {
    const patch = gameStatePatchFromMapManagerInit(initialState);
    if (patch) {
      dispatchGameState({ type: GAME_SESSION_PATCH, payload: patch });
    }
  }, [initialState]);

  const socketHook = useSocket({
    grid,
    tokens,
    room,
    maps: mapsSlice,
    navigate,
    isActivatingFromLocalRef,
    getViewingMapId,
    purgeLocalSessionAfterRoomEnd,
    onRoomDeleted: (message) => {
      setRoomDeletedMessage(message);
      setShowRoomDeletedDialog(true);
    },
  });

  const sync = {
    roomId,
    viewingMapId,
    currentMapId,
    socketHook,
  };

  const handleActivateMap = useCallback(
    (mapId, coinsToTransfer = []) => {
      handleActivateMapInternal(mapId, coinsToTransfer, socketHook);
    },
    [handleActivateMapInternal, socketHook]
  );

  const tokenSession = useTokenSession({
    tokens,
    sync,
    gridSize: { gridWidth, gridHeight },
    refs: { gameContainerRef, gameGridRef },
  });

  const gridUi = {
    selectedPaintColor,
    isPaintMode: mode === MODE.PAINT,
    paintMode,
    mode,
    categoryMode,
  };
  const gridSession = useGridSession({
    grid,
    ui: gridUi,
    selection: { selectedObjectId, setSelectedObjectId },
    sync,
  });

  const handleInitiativeUpdate = useCallback(
    (newOrder) => {
      const sortedOrder = sortInitiativeOrder(newOrder);
      setInitiativeOrder(sortedOrder);
      syncActiveMapState({
        roomId,
        viewingMapId,
        currentMapId,
        socketHook,
        patch: { initiativeOrder: sortedOrder },
      });
    },
    [roomId, socketHook, viewingMapId, currentMapId, setInitiativeOrder]
  );

  const handleColorSelect = useCallback(
    (
      color,
      isEnemy,
      image,
      isLarge,
      name,
      maxHealth,
      armorClass,
      monsterData = null,
      level = null,
      isPc,
      options = {}
    ) => {
      const newCoin = createBaseCoin({
        color,
        isEnemy,
        image,
        isLarge,
        name,
        maxHealth,
        armorClass,
        monsterData,
        level: isEnemy ? undefined : level,
        isPc: isEnemy ? undefined : isPc,
      });

      addCoinsToState({
        newCoins: [newCoin],
        currentCoins: coins,
        currentInitiativeOrder: initiativeOrder,
        roomId,
        viewingMapId,
        currentMapId,
        socketHook,
        setCoins,
        setInitiativeOrder,
      });

      if (mode === MODE.TOKEN) {
        setNewlyCreatedCoinId(newCoin.id);
      }

      if (!options?.keepModalOpen) {
        setShowColorModal(false);
      }
    },
    [coins, initiativeOrder, roomId, viewingMapId, currentMapId, socketHook, mode, setCoins, setInitiativeOrder]
  );

  const handleColorSelectBatch = useCallback(
    (entries = []) => {
      if (!Array.isArray(entries) || entries.length === 0) {
        setShowColorModal(false);
        return;
      }

      const newCoins = [];
      entries.forEach((entry) => {
        const count = Math.max(1, parseInt(entry?.count, 10) || 1);
        const payload = entry?.payload || {};
        for (let i = 0; i < count; i += 1) {
          newCoins.push(
            createBaseCoin({
              color: payload.color,
              isEnemy: payload.isEnemy,
              image: payload.image,
              isLarge: payload.isLarge,
              name: payload.name,
              maxHealth: payload.maxHealth,
              armorClass: payload.armorClass,
              monsterData: payload.monsterData ?? null,
              level: payload.isEnemy ? undefined : payload.level ?? null,
              isPc: payload.isEnemy ? undefined : payload.isPc,
            })
          );
        }
      });

      if (newCoins.length === 0) {
        setShowColorModal(false);
        return;
      }

      addCoinsToState({
        newCoins,
        currentCoins: coins,
        currentInitiativeOrder: initiativeOrder,
        roomId,
        viewingMapId,
        currentMapId,
        socketHook,
        setCoins,
        setInitiativeOrder,
      });

      if (mode === MODE.TOKEN) {
        setNewlyCreatedCoinId(newCoins[0].id);
      }

      setShowColorModal(false);
    },
    [coins, initiativeOrder, roomId, viewingMapId, currentMapId, socketHook, mode, setCoins, setInitiativeOrder]
  );

  const handleAddEncounterMonsters = useCallback(
    (encounter) => {
      const newCoins = createCoinsFromEncounter(encounter);
      if (newCoins.length === 0) return;

      addCoinsToState({
        newCoins,
        currentCoins: coins,
        currentInitiativeOrder: initiativeOrder,
        roomId,
        viewingMapId,
        currentMapId,
        socketHook,
        setCoins,
        setInitiativeOrder,
      });

      if (mode === MODE.TOKEN) {
        setNewlyCreatedCoinId(newCoins[0].id);
      }
    },
    [coins, initiativeOrder, roomId, viewingMapId, currentMapId, socketHook, mode, setCoins, setInitiativeOrder]
  );

  const handlePaintColorSelect = useCallback((color) => {
    setSelectedPaintColor(color);
    setShowPaintModal(false);
  }, []);

  const handleGridMouseDown = useCallback(
    (i, j, meta) => {
      if (mode === MODE.PAINT) {
        if (gridSession.contextMenuObject) {
          gridSession.handleCloseContextMenu();
          return;
        }
        gridSession.handleGridMouseDown(i, j, meta);
      }
    },
    [mode, gridSession]
  );

  useEffect(() => {
    const timeout = updateTimeout.current;
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, []);

  const handleClearGrid = useCallback(() => {
    const partial = buildClearGridGameState(coins, initiativeOrder);
    dispatchGameState({ type: GAME_SESSION_PATCH, payload: partial });
    setSelectedObjectId(null);
    syncActiveMapState({
      roomId,
      viewingMapId,
      currentMapId,
      socketHook,
      patch: partial,
    });
  }, [coins, initiativeOrder, roomId, viewingMapId, currentMapId, socketHook, dispatchGameState]);

  const handleSelectBattlemap = useCallback(
    (battlemapId, options = {}) => {
      const { emitUpdate = true, gridWidthOverride, gridHeightOverride } = options;

      setSelectedBattlemapId(battlemapId);

      const selectedBattlemap = getBattlemapById(battlemapId);
      let newGridWidth = gridWidthOverride ?? gridWidth;
      let newGridHeight = gridHeightOverride ?? gridHeight;

      if (
        gridWidthOverride === undefined &&
        gridHeightOverride === undefined &&
        selectedBattlemap &&
        selectedBattlemap.gridWidth > 0 &&
        selectedBattlemap.gridHeight > 0
      ) {
        newGridWidth = selectedBattlemap.gridWidth;
        newGridHeight = selectedBattlemap.gridHeight;
      }

      setGridWidth(newGridWidth);
      setGridHeight(newGridHeight);

      if (emitUpdate) {
        syncActiveMapState({
          roomId,
          viewingMapId,
          currentMapId: viewingMapId,
          socketHook,
          patch: {
            battlemapId,
            gridWidth: newGridWidth,
            gridHeight: newGridHeight,
          },
        });
      }
    },
    [roomId, viewingMapId, gridWidth, gridHeight, socketHook, setSelectedBattlemapId, setGridWidth, setGridHeight]
  );

  const handleDeselectCoin = useCallback(() => {
    setSelectedCoinId(null);
    setSelectedObjectId(null);
  }, []);

  const handleCopyCoin = useCallback(
    (coin) => {
      tokenSession.handleCoinCopy(coin);
      setSelectedCoinId(null);
    },
    [tokenSession]
  );

  const handleDeleteCoinSidebar = useCallback(
    (coin) => {
      tokenSession.handleDragEnd(null, coin, true);
      setSelectedCoinId(null);
    },
    [tokenSession]
  );

  const handleCoinDragEnd = useCallback(
    (coin, newPos) => {
      const updatedCoins = coins.map((c) =>
        c.id === coin.id ? { ...c, x: newPos.x, y: newPos.y } : c
      );
      setCoins(updatedCoins);

      setInitiativeOrder((prevOrder) =>
        prevOrder.map((c) => (c.id === coin.id ? { ...c, x: newPos.x, y: newPos.y } : c))
      );

      const updatedOrder = initiativeOrder.map((c) =>
        c.id === coin.id ? { ...c, x: newPos.x, y: newPos.y } : c
      );
      syncActiveMapState({
        roomId,
        viewingMapId,
        currentMapId,
        socketHook,
        patch: {
          coins: updatedCoins,
          initiativeOrder: updatedOrder,
        },
      });
    },
    [coins, initiativeOrder, roomId, viewingMapId, currentMapId, socketHook, setCoins, setInitiativeOrder]
  );

  const handleGridSelectObject = useCallback((objectId) => {
    setSelectedObjectId(objectId);
    if (objectId) {
      setSelectedCoinId(null);
    }
  }, []);

  const handleGridSelectCoin = useCallback(
    (coinId) => {
      setSelectedCoinId(coinId);
      if (coinId) {
        setSelectedObjectId(null);
      }
      if (mode !== MODE.TOKEN) {
        setMode(MODE.CURSOR);
      }
      setShowColorModal(false);
      setShowPaintModal(false);
    },
    [mode]
  );

  const handleGridDeselectAll = useCallback(() => {
    setSelectedCoinId(null);
    setSelectedObjectId(null);
  }, []);

  useEffect(() => {
    if (mode === MODE.TOKEN && cursorMode !== CURSOR_MODE.GAME) {
      setCursorMode(CURSOR_MODE.GAME);
    }
  }, [mode, cursorMode]);

  const handleMapBarActivate = useCallback(() => {
    if (gameSidebarRef.current?.requestMapActivation) {
      gameSidebarRef.current.requestMapActivation(viewingMapId);
    } else {
      handleActivateMap(viewingMapId);
    }
  }, [viewingMapId, handleActivateMap]);

  const handleDeleteTokenFromPanel = useCallback(
    (coin) => {
      tokenSession.handleDragEnd(null, coin, true);
    },
    [tokenSession]
  );

  const handleOverlayConfirmDelete = useCallback(() => {
    const coin = coins.find((c) => c.id === selectedCoinId);
    if (coin) {
      tokenSession.handleDragEnd(null, coin, true);
      setSelectedCoinId(null);
    }
  }, [coins, selectedCoinId, tokenSession]);

  const handleOverlayConfirmRemove = useCallback(() => {
    tokenSession.handleRemoveFromGrid(selectedCoinId);
    setSelectedCoinId(null);
  }, [selectedCoinId, tokenSession]);

  const openCoinHealthDialog = useCallback(() => setShowHealthDialog(true), []);
  const openCoinStatusDialog = useCallback(() => setShowStatusDialog(true), []);
  const openCoinDeleteConfirm = useCallback(() => setShowDeleteConfirm(true), []);
  const openCoinRemoveConfirm = useCallback(() => setShowRemoveConfirm(true), []);
  const openCoinStatBlockDialog = useCallback(() => setShowStatBlockDialog(true), []);

  const openCurrentMapEditor = useCallback(() => {
    gameSidebarRef.current?.openMapEditorForCurrentMap?.();
  }, []);

  const sessionFlowRef = useRef({});

  const getRoomGameStateForPayload = useCallback(
    () => ({
      viewingMapId,
      currentMapId,
      maps,
      gridColors,
      gridWalls,
      gridObjects,
      gridObjectInstances,
      coins,
      initiativeOrder,
      selectedBattlemapId,
    }),
    [
      viewingMapId,
      currentMapId,
      maps,
      gridColors,
      gridWalls,
      gridObjects,
      gridObjectInstances,
      coins,
      initiativeOrder,
      selectedBattlemapId,
    ]
  );

  sessionFlowRef.current = {
    roomId,
    userName,
    isRoomCreator,
    dispatchGameState,
    socketHook,
    setRoomId,
    setUserName,
    setRoomCreator,
    setPermissions,
    setConnectedUsers,
    navigate,
    getRoomGameStateForPayload,
    maps,
    setMaps,
    setCurrentMapId,
    setViewingMapId,
    cancelPendingMapLocalStorageSave: cancelPendingMapPersistence,
    currentMapId,
    viewingMapId,
    onViewMap: handleViewMap,
    onActivateMap: handleActivateMap,
    onSelectBattlemap: handleSelectBattlemap,
    generateMapId: uuidv4,
    setInitiativeOrder,
  };

  const sessionFlows = useMemo(
    () => ({
      applyJoinRoomResponse: (response) =>
        applyRoomStateFromResponse(response, {
          dispatchGameState: (action) => sessionFlowRef.current.dispatchGameState(action),
          setRoomCreator: (v) => sessionFlowRef.current.setRoomCreator(v),
          setInitiativeOrder: (v) => sessionFlowRef.current.setInitiativeOrder(v),
          setPermissions: (v) => sessionFlowRef.current.setPermissions(v),
        }),
      saveSettings: (params) => saveSettingsFlow(sessionFlowRef.current, params),
      leaveRoom: () => leaveRoomFlow(sessionFlowRef.current),
      saveMapEditor: (editorParams) => saveMapEditorFlow(sessionFlowRef.current, editorParams),
      duplicateMap: (mapId) => duplicateMapFlow(sessionFlowRef.current, mapId),
      deleteMap: (selectedMapId) => deleteMapFlow(sessionFlowRef.current, selectedMapId),
    }),
    []
  );

  const gameContextValue = useMemo(
    () => ({
      mode,
      setMode,
      cursorMode,
      setCursorMode,
      selectedCoin: coins.find((c) => c.id === selectedCoinId) || null,
      selectedObjectId,
      onDeselectCoin: handleDeselectCoin,
      onCopyCoin: handleCopyCoin,
      onDeleteCoin: handleDeleteCoinSidebar,
      onToggleHidden: tokenSession.handleToggleHidden,
      onRemoveFromGrid: tokenSession.handleRemoveFromGrid,
      onEditCoin: tokenSession.handleEdit,
      onEditCoinHealth: tokenSession.handleEditHealth,
      onEditCoinStatus: tokenSession.handleEditStatus,
      onRotateObject: gridSession.handleRotateObject,
      onDeleteObject: gridSession.handleDeleteObject,
      paintMode,
      setPaintMode,
      categoryMode,
      setCategoryMode,
      selectedPaintColor,
      onPaintColorSelect: handlePaintColorSelect,
      onClearColors: gridSession.handleClearAllColors,
      onClearGrid: handleClearGrid,
      showClearGrid: !roomId || isRoomCreator,
      isCreator: isRoomCreator,
      canManageCharacters: isRoomCreator || permissions.allowCharacterManagement,
      canManageEnemies: isRoomCreator || permissions.allowEnemyManagement,
      canPaint: isRoomCreator || permissions.allowPainting,
      roomId,
      setRoomId,
      userName,
      setUserName,
      permissions,
      setPermissions,
      connectedUsers,
      setConnectedUsers,
      gridWidth,
      setGridWidth,
      gridHeight,
      setGridHeight,
      socketHook,
      coins,
      setCoins,
      gridColors,
      setGridColors,
      gridWalls,
      setGridWalls,
      gridObjects,
      setGridObjects,
      gridObjectInstances,
      setGridObjectInstances,
      initiativeOrder,
      onInitiativeUpdate: handleInitiativeUpdate,
      maps,
      setMaps,
      currentMapId,
      viewingMapId,
      onViewMap: handleViewMap,
      onActivateMap: handleActivateMap,
      setRoomCreator,
      setInitiativeOrder,
      showRoomDeletedDialog,
      selectedBattlemapId,
      onSelectBattlemap: handleSelectBattlemap,
      selectedCoinId,
      setSelectedCoinId,
      zoom,
      setZoom,
      dispatchGameState,
      onCoinDragEnd: handleCoinDragEnd,
      onGridSelectObject: handleGridSelectObject,
      onGridSelectCoin: handleGridSelectCoin,
      onGridDeselectAll: handleGridDeselectAll,
      onCoinHealthAction: openCoinHealthDialog,
      onCoinStatusAction: openCoinStatusDialog,
      onCoinRemoveAction: openCoinRemoveConfirm,
      onCoinDeleteAction: openCoinDeleteConfirm,
      onCoinStatBlockAction: openCoinStatBlockDialog,
      areaPaintStart: gridSession.areaPaintStart,
      areaPaintEnd: gridSession.areaPaintEnd,
      wallHoverCorner: gridSession.wallHoverCorner,
      wallDragStartCorner: gridSession.wallDragStartCorner,
      wallDragEndCorner: gridSession.wallDragEndCorner,
      onGridMouseDown: handleGridMouseDown,
      onGridMouseEnter: gridSession.handleGridMouseEnter,
      onGridMouseUp: gridSession.handleGridMouseUp,
      onGridMouseLeave: gridSession.handleGridMouseLeave,
      onObjectDragEnd: gridSession.handleObjectDragEnd,
      objectContextMenuPosition: gridSession.contextMenuPosition,
      objectContextMenuObject: gridSession.contextMenuObject,
      onContextMenuRotate: gridSession.handleContextRotateObject,
      onContextMenuDelete: gridSession.handleContextDeleteObject,
      showColorModal,
      setShowColorModal,
      handleColorSelect,
      handleColorSelectBatch,
      showPaintModal,
      setShowPaintModal,
      handleAddEncounterMonsters,
      newlyCreatedCoinId,
      setNewlyCreatedCoinId,
      onTokenDragStart: tokenSession.handleTokenDragStart,
      onTokenDragEnd: tokenSession.handleTokenDragEnd,
      onDeleteTokenFromPanel: handleDeleteTokenFromPanel,
      showHealthDialog,
      showStatusDialog,
      showStatBlockDialog,
      showDeleteConfirm,
      showRemoveConfirm,
      setShowHealthDialog,
      setShowStatusDialog,
      setShowStatBlockDialog,
      setShowDeleteConfirm,
      setShowRemoveConfirm,
      onOverlayConfirmDelete: handleOverlayConfirmDelete,
      onOverlayConfirmRemove: handleOverlayConfirmRemove,
      handleMapBarActivate,
      roomDeletedMessage,
      setRoomDeletedMessage,
      setShowRoomDeletedDialog,
      openCurrentMapEditor,
      tokenDragPreviewCoin: tokenSession.draggingToken,
      tokenDragPreviewX: tokenSession.dragPosition.x,
      tokenDragPreviewY: tokenSession.dragPosition.y,
      tokenDragPreviewVisible: !!tokenSession.draggingToken,
      sessionFlows,
    }),
    [
      mode,
      cursorMode,
      coins,
      selectedCoinId,
      selectedObjectId,
      handleDeselectCoin,
      handleCopyCoin,
      handleDeleteCoinSidebar,
      tokenSession,
      gridSession,
      paintMode,
      categoryMode,
      selectedPaintColor,
      handlePaintColorSelect,
      handleClearGrid,
      roomId,
      isRoomCreator,
      permissions,
      userName,
      connectedUsers,
      gridWidth,
      gridHeight,
      socketHook,
      gridColors,
      gridWalls,
      gridObjects,
      gridObjectInstances,
      initiativeOrder,
      handleInitiativeUpdate,
      maps,
      currentMapId,
      viewingMapId,
      handleViewMap,
      handleActivateMap,
      showRoomDeletedDialog,
      selectedBattlemapId,
      handleSelectBattlemap,
      setMaps,
      handleCoinDragEnd,
      handleGridSelectObject,
      handleGridSelectCoin,
      handleGridDeselectAll,
      openCoinHealthDialog,
      openCoinStatusDialog,
      openCoinRemoveConfirm,
      openCoinDeleteConfirm,
      openCoinStatBlockDialog,
      handleGridMouseDown,
      handleColorSelect,
      handleColorSelectBatch,
      handleAddEncounterMonsters,
      newlyCreatedCoinId,
      handleDeleteTokenFromPanel,
      showHealthDialog,
      showStatusDialog,
      showStatBlockDialog,
      showDeleteConfirm,
      showRemoveConfirm,
      handleOverlayConfirmDelete,
      handleOverlayConfirmRemove,
      handleMapBarActivate,
      roomDeletedMessage,
      zoom,
      showColorModal,
      showPaintModal,
      openCurrentMapEditor,
      dispatchGameState,
      setGridWidth,
      setGridHeight,
      setGridColors,
      setGridWalls,
      setGridObjects,
      setGridObjectInstances,
      setCoins,
      setInitiativeOrder,
      sessionFlows,
    ]
  );

  return {
    gameContextValue,
    gameContainerRef,
    gameGridRef,
    gameSidebarRef,
    mode,
  };
}
